import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiArrowLeft } from 'react-icons/hi';

import { getPublishedPosts, isMissingBlogTable } from '../services/blogApi';
import { trackEvent } from '../utils/analytics';
import '../styles/Blog.css';

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    getPublishedPosts().then(({ data, error }) => {
      if (cancelled) return;
      setPosts(data);
      setError(error);
      setLoading(false);

      // How many articles the reader was actually offered.
      if (!error) trackEvent('view_blog_list', { post_count: data.length });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="blog-page">
      <div className="blog-header">
        <button className="back-to-home-btn" onClick={() => navigate('/')}>
          <HiArrowLeft />
          <span>Back to Home</span>
        </button>

        <h1>Blog</h1>
        <p>
          Notes on gemstones, planets and how to wear them — written by our
          team.
        </p>
      </div>

      <div className="blog-list">
        {loading ? (
          <div className="blog-grid">
            {Array.from({ length: 3 }).map((_, i) => (
              <div className="blog-skeleton" key={i} />
            ))}
          </div>
        ) : isMissingBlogTable(error) ? (
          <p className="blog-empty">
            The blog isn't set up yet. Run{' '}
            <code>src/backend/sql/blog.sql</code> in the Supabase SQL editor.
          </p>
        ) : posts.length === 0 ? (
          <p className="blog-empty">
            No articles yet — the first one is on its way.
          </p>
        ) : (
          <div className="blog-grid">
            {posts.map((post) => (
              <motion.article
                key={post.id}
                className="blog-card"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.35 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="blog-card-link"
                  onClick={() =>
                    trackEvent('select_blog_post', {
                      post_slug: post.slug,
                      post_title: post.title,
                    })
                  }
                >
                  <div className="blog-card-image">
                    {post.cover_image ? (
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        loading="lazy"
                      />
                    ) : (
                      <div className="blog-card-fallback">✧</div>
                    )}
                  </div>

                  <div className="blog-card-body">
                    <time className="blog-card-date">
                      {fmtDate(post.published_at || post.created_at)}
                    </time>
                    <h2>{post.title}</h2>
                    {post.excerpt && <p>{post.excerpt}</p>}
                    <span className="blog-card-more">Read article →</span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
